import { useNavigate } from 'react-router-dom';
import { 
  Users, TrendingUp, BarChart3, Mail, Shield, Zap,
  Target, PieChart, Calendar, MessageSquare, ArrowRight,
  CheckCircle2, Sparkles
} from 'lucide-react';

const CRMShowcase = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Target,
      title: 'Lead Management',
      description: 'Capture, track, and nurture leads through your entire sales funnel',
      color: '#3B82F6'
    },
    {
      icon: TrendingUp,
      title: 'Deal Pipeline',
      description: 'Visual Kanban board to manage deals from prospect to close',
      color: '#8B5CF6'
    },
    {
      icon: Users,
      title: 'Contact Management',
      description: 'Centralized database for all your customer relationships',
      color: '#10B981'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reports',
      description: 'Real-time insights to make data-driven decisions',
      color: '#F97316'
    },
    {
      icon: Calendar,
      title: 'Activity Timeline',
      description: 'Track all interactions and never miss a follow-up',
      color: '#EC4899'
    },
    {
      icon: Shield,
      title: 'Team Collaboration',
      description: 'Role-based access with activity tracking for your team',
      color: '#06B6D4'
    }
  ];

  const benefits = [
    'Close deals 40% faster',
    'Never lose a lead again',
    'Real-time sales insights',
    '24/7 customer support',
    'Easy team onboarding',
    'Data export anytime'
  ];

  return (
    <section id="crm" className="py-24 px-[7.6923%] relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[150px]"></div>
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[150px]"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-semibold text-blue-400">NEW PRODUCT</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Powerful CRM Software for
            <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent"> Growing Businesses</span>
          </h2>
          
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            Streamline your sales process, manage customer relationships, and grow your revenue 
            with our intuitive CRM platform. Built for teams of all sizes.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group p-6 bg-slate-800/30 backdrop-blur border border-slate-700/50 rounded-2xl hover:border-slate-600/50 transition-all duration-300 hover:-translate-y-1"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                style={{ background: `${feature.color}15`, border: `1px solid ${feature.color}30` }}
              >
                <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
              </div>
              
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl blur-xl"></div>
          
          <div className="relative bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left - Benefits */}
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                  Why Choose Our CRM?
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span className="text-slate-300 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - Pricing Preview & CTA */}
              <div className="text-center md:text-left">
                <div className="inline-block mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-slate-400 text-lg">Starting at</span>
                    <span className="text-4xl font-bold text-white">$19</span>
                    <span className="text-slate-400">/month</span>
                  </div>
                  <p className="text-sm text-slate-500">14-day free trial • No credit card required</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                  <button
                    onClick={() => navigate('/crm/pricing')}
                    className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20"
                    data-testid="crm-view-plans-btn"
                  >
                    View Plans & Pricing
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  
                  <button
                    onClick={() => navigate('/crm/pricing')}
                    className="px-8 py-4 bg-slate-700/50 hover:bg-slate-700 text-white font-medium rounded-xl border border-slate-600 transition-all"
                    data-testid="crm-start-trial-btn"
                  >
                    Start Free Trial
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CRMShowcase;
